/* ============================================================
   Product registry
   ------------------------------------------------------------
   Single source of truth for the two products that anchor the
   page: Theo (the AI engine) and OpenCharts (the AI-native
   workspace). All copy lives here so updates don't require
   chasing JSX.
   ============================================================ */

export const PRODUCTS = [
  {
    id: 'theo',
    name: 'Theo',
    tag: 'The AI engine',
    eyebrow: 'AI engine',
    accent: 'var(--theo-c)',
    pip: 'linear-gradient(95deg, var(--theo-a), var(--theo-c))',
    stage: 'stage-theo',
    span: 'wide',
    blurb:
      'The AI engine powering reasoning, generation, and workflow execution across the system.',
    headline:
      'Theo powers the entire system. It understands inputs, generates structured outputs, and executes workflows across diagrams, code, and content.',
    valueProp:
      'AI engine for building, reasoning, and execution.',
    problem:
      'Modern software needs an engine that can read intent, choose the right model for the task, and execute workflows end-to-end. Theo collapses that into a single intelligence layer: prompts come in, structured outputs come out, and execution happens across every surface in the product.',
    features: [
      {
        title: 'Multi-modal AI engine',
        body:
          'A single brain across text, code, image, and voice. Theo classifies intent, picks the right model, and returns structured output you can ship straight into a product surface.',
      },
      {
        title: 'Prompt → structured system output',
        body:
          'Describe what you need; Theo returns a graph, a slide deck, a flowchart, a landing page, or a code module — typed and ready for the next step in the workflow.',
      },
      {
        title: 'Execution-ready workflows',
        body:
          'Theo doesn\'t just generate. It runs. Tool use, multi-step planning, and deterministic handoff into downstream systems mean output becomes action.',
      },
      {
        title: 'Cross-surface intelligence',
        body:
          'One engine, every surface. Theo powers OpenCharts canvases, presentations, vibe-coded pages, notes, and podcasts from the same model layer.',
      },
    ],
    idealBuyer: {
      summary:
        'Product and engineering teams who want a single AI layer that can reason, generate, and execute, instead of bolting together half a dozen vendor SDKs.',
      bullets: [
        'Teams shipping AI features that need real reasoning and structured output, not just chat',
        'Founders and platform engineers who need a model-agnostic backbone before committing to a single provider',
        'Operators who need AI to drive workflows end-to-end, not just answer questions',
      ],
    },
    outcomes: [
      { metric: '1',     label: 'Brain' },
      { metric: '15+',   label: 'Models' },
      { metric: '5',     label: 'Channels' },
      { metric: '700B+', label: 'Parameters' },
      { metric: '0',     label: 'Code' },
    ],
    presentationLink: 'https://www.hitheo.ai/',
    ctaLabel: 'Book walkthrough demo',
  },
  {
    id: 'charts',
    name: 'OpenCharts',
    tag: 'The AI-native workspace',
    eyebrow: 'AI workspace',
    accent: 'var(--charts)',
    pip: 'var(--charts)',
    stage: 'stage-charts',
    span: '',
    blurb:
      'AI-native workspace for flowcharts, presentations, vibe-coded landing pages, podcasts, and notes — one canvas, powered by Theo.',
    headline:
      'OpenCharts turns ideas into flowcharts, presentations, and working products. Describe your workflow and Theo builds it instantly.',
    valueProp:
      'Flowcharts, presentations, vibe coding, podcasts, notes — one workspace, AI-native.',
    problem:
      'Teams use Miro for whiteboarding, Lucidchart for diagrams, Notion for docs, Slides for decks, and a separate tool to record podcasts. Five subscriptions, no AI baked in, no shared canvas. OpenCharts collapses all of it into one AI-native workspace where Theo turns a prompt into a diagram, a deck, a working landing page, a podcast, or a note.',
    features: [
      {
        title: 'Flowcharts',
        body:
          'Describe a process and Theo builds the whole diagram. Twenty-plus node types, swimlanes, decision branches, smart connections, and exports to PNG, SVG, and Mermaid.',
      },
      {
        title: 'Presentations',
        body:
          'The same canvas becomes a deck. Each node maps to a slide or step, transitions are controlled by graph traversal, and your flow becomes your presentation in one click.',
      },
      {
        title: 'Vibe coding for landing pages',
        body:
          'A flowchart becomes a working product. Theo generates landing pages, dashboards, and internal tools from the canvas, with live preview and a code view side-by-side.',
      },
      {
        title: 'Podcasts',
        body:
          'Turn notes, docs, or a flowchart into a fully produced podcast episode. Training-ready audio outputs in minutes, not weeks.',
      },
      {
        title: 'Notes',
        body:
          'Structured knowledge that lives next to the canvas. Capture, organize, and let Theo turn long-form notes into diagrams, decks, or podcasts on demand.',
      },
    ],
    idealBuyer: {
      summary:
        'Product, design, and operations teams who want one AI-native workspace for the visual artifacts they ship every day — diagrams, decks, pages, podcasts, and notes.',
      bullets: [
        'Product and design teams mapping flows, decisions, and architectures on a shared canvas',
        'Founders shipping landing pages and internal tools without standing up a frontend stack',
        'Marketing and content teams turning ideas into decks, podcasts, and notes in one workflow',
      ],
    },
    outcomes: [
      { metric: '5',    label: 'Surfaces, one canvas' },
      { metric: '20+',  label: 'Node types per diagram' },
      { metric: 'Live', label: 'Real-time collaboration' },
      { metric: 'AI',   label: 'Native, not bolted on' },
    ],
    presentationLink: 'https://www.opencharts.com/',
    ctaLabel: 'Book walkthrough demo',
  },
];

/**
 * Look up a product by id.
 * @param {string} id
 * @returns {object | undefined}
 */
export function getProduct(id) {
  return PRODUCTS.find((p) => p.id === id);
}

/**
 * Returns true if the given string still looks like a {{PLACEHOLDER}}.
 * Useful for hiding the presentation-link button until you fill in
 * a real URL in `products.js`.
 */
export function isPlaceholder(value) {
  return typeof value === 'string' && /^\{\{[A-Z0-9_]+\}\}$/.test(value.trim());
}
